import { Fragment, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArticleDetail } from '../components/ArticleDetail'
import { CopyFallbackDialog } from '../components/CopyFallbackDialog'
import { VocabularyDialog } from '../components/VocabularyDialog'
import { getItemStates, markItemOpened, setItemInterest, setItemSaved } from '../data/db'
import { prefetchContextVocabulary } from '../data/vocabularyLookup'
import { useVocabularyLookup } from '../hooks/useVocabularyLookup'
import type { DailyIssue, InterestValue, ItemState, IssueItem, VocabularyEntry } from '../types/daily'
import { shareDiscussion } from '../utils/discussion'

interface TodayPageProps {
  issue?: DailyIssue
  loading: boolean
  source?: 'network' | 'cache'
  warning?: string
  refresh: () => Promise<void>
}

const formatIssueDate = (value: string) => new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: 'long', day: 'numeric', weekday: 'long', timeZone: 'Asia/Singapore',
}).format(new Date(`${value}T00:00:00+08:00`))

export function TodayPage({ issue, loading, source, warning, refresh }: TodayPageProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [itemStates, setItemStates] = useState<Record<string, ItemState>>({})
  const vocabularyLookup = useVocabularyLookup()
  const [copyFallback, setCopyFallback] = useState<string>()
  const [toast, setToast] = useState<string>()
  const openItemId = searchParams.get('item')

  const reloadItemStates = async (issueId: string) => {
    const records = await getItemStates(issueId)
    setItemStates(Object.fromEntries(records.map((record) => [record.itemId, record])))
  }

  useEffect(() => {
    if (issue) {
      void reloadItemStates(issue.issueId)
      void prefetchContextVocabulary(issue.date)
    }
  }, [issue])

  useEffect(() => {
    if (!issue || !openItemId || !issue.items.some((item) => item.id === openItemId)) return
    const item = issue.items.find((candidate) => candidate.id === openItemId)
    if (item) void markItemOpened(issue.issueId, issue.date, item).then(() => reloadItemStates(issue.issueId))
    requestAnimationFrame(() => document.getElementById(`article-${openItemId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }, [issue, openItemId])

  useEffect(() => {
    if (!toast) return
    const timeout = window.setTimeout(() => setToast(undefined), 2400)
    return () => window.clearTimeout(timeout)
  }, [toast])

  const readCount = useMemo(() => Object.values(itemStates).filter((state) => state.firstOpenedAt).length, [itemStates])

  if (loading) {
    return <section className="loading-state" aria-label="正在加载日报"><span /><span /><span /></section>
  }

  if (!issue) {
    return (
      <section className="empty-state page-enter">
        <p className="eyebrow">Daily briefing</p>
        <h1>暂时无法打开日报</h1>
        <p>{warning ?? '请连接网络后重试。'}</p>
        <button type="button" className="primary-button" onClick={() => void refresh()}>重新加载</button>
      </section>
    )
  }

  const toggleItem = (itemId: string) => {
    if (openItemId === itemId) setSearchParams({}, { replace: true })
    else setSearchParams({ item: itemId })
  }

  const updateSaved = async (item: IssueItem, saved: boolean) => {
    const next = await setItemSaved(issue.issueId, issue.date, item, saved)
    setItemStates((current) => ({ ...current, [item.id]: next }))
    setToast(saved ? '已加入收藏' : '已取消收藏')
  }

  const updateInterest = async (item: IssueItem, interest: InterestValue) => {
    const next = await setItemInterest(issue.issueId, issue.date, item, interest)
    setItemStates((current) => ({ ...current, [item.id]: next }))
    setToast(interest === 'interested' ? '已记录：感兴趣' : interest === 'not_interested' ? '已记录：不感兴趣' : '已撤销反馈')
  }

  const openVocabulary = async (item: IssueItem, vocabulary: VocabularyEntry) => {
    await vocabularyLookup.openCurated(issue.issueId, issue.date, item, vocabulary)
  }

  const toggleVocabularySaved = async (saved: boolean) => {
    await vocabularyLookup.toggleSaved(saved)
    setToast(saved ? '表达已收藏' : '已取消表达收藏')
  }

  const discuss = async (item: IssueItem) => {
    const result = await shareDiscussion(item)
    if (result.status === 'shared') setToast('已打开系统分享')
    if (result.status === 'copied') setToast('讨论上下文已复制')
    if (result.status === 'manual') setCopyFallback(result.value)
  }

  return (
    <div className="today-page page-enter">
      {warning && <aside className="notice notice--warning" role="status"><span>{warning}</span><button type="button" onClick={() => void refresh()}>重试</button></aside>}

      <header className="issue-hero">
        <div className="issue-hero__date">
          <p className="eyebrow">Daily briefing · {source === 'cache' ? '离线阅读' : '今日更新'}</p>
          <time dateTime={issue.date}>{formatIssueDate(issue.date)}</time>
        </div>
        <h1>今天值得关注的<br /><em>{issue.items.length} 件事</em></h1>
        <p className="issue-hero__lead">{issue.editorial.lead}</p>
        <div className="issue-stats" aria-label="本期概况">
          <span><strong>{issue.editorial.estimatedMinutes}</strong> 分钟</span>
          <span><strong>{issue.editorial.counts.english}</strong> 条英文</span>
          <span><strong>{readCount}/{issue.items.length}</strong> 已打开</span>
        </div>
      </header>

      {issue.readerNotice && <aside className="notice notice--reader" role="note">{issue.readerNotice}</aside>}

      <section className="reading-plan" aria-labelledby="reading-plan-title">
        <p className="eyebrow">Reading route</p>
        <h2 id="reading-plan-title">30 分钟阅读路线</h2>
        <ol>{issue.editorial.readingPlan.map((step) => <li key={step}>{step}</li>)}</ol>
      </section>

      <section className="issue-index" aria-labelledby="issue-index-title">
        <div className="section-heading">
          <div><p className="eyebrow">The briefing</p><h2 id="issue-index-title">今日必读</h2></div>
          <span>点击展开</span>
        </div>

        <div className="issue-index__list">
          {issue.items.map((item) => {
            const isOpen = openItemId === item.id
            return (
              <Fragment key={item.id}>
                <button type="button" className={`issue-index__item${isOpen ? ' is-open' : ''}`} onClick={() => toggleItem(item.id)} aria-expanded={isOpen} aria-controls={`article-${item.id}`}>
                  <span className="issue-index__rank">{String(item.rank).padStart(2, '0')}</span>
                  <span className="issue-index__copy">
                    <span className="issue-index__source">{item.source.name} · {item.estimatedMinutes} 分钟 {item.featuredEnglish ? '· 英文重点' : ''}</span>
                    <strong lang={item.source.language === 'en' ? 'en' : 'zh-CN'}>{item.title}</strong>
                  </span>
                  <span className="issue-index__toggle" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && (
                  <ArticleDetail
                    item={item}
                    state={itemStates[item.id]}
                    onSaved={(saved) => void updateSaved(item, saved)}
                    onInterest={(interest) => void updateInterest(item, interest)}
                    onVocabulary={(entry) => void openVocabulary(item, entry)}
                    onLookupSelection={(selection) => void vocabularyLookup.lookupSelection(issue.issueId, issue.date, item, selection)}
                    onDiscuss={() => void discuss(item)}
                  />
                )}
              </Fragment>
            )
          })}
        </div>
      </section>

      <section className="trend-section" aria-labelledby="trend-title">
        <div className="section-heading"><div><p className="eyebrow">Trend radar</p><h2 id="trend-title">趋势雷达</h2></div><span>{issue.trendSignals.length} 条信号</span></div>
        <div className="trend-grid">{issue.trendSignals.map((trend, index) => <article key={trend.id}><span>{String(index + 1).padStart(2, '0')}</span><h3>{trend.title}</h3><p>{trend.update}</p><details><summary>接下来关注什么</summary><p>{trend.whyWatch}</p></details></article>)}</div>
      </section>

      {issue.more.length > 0 && <section className="more-section" aria-labelledby="more-title">
        <div className="section-heading"><div><p className="eyebrow">On the radar</p><h2 id="more-title">更多动态</h2></div><span>快速扫描</span></div>
        <div className="more-list">{issue.more.map((item) => <a key={item.url} href={item.url} target="_blank" rel="noreferrer noopener"><span>{item.sourceName}</span><strong lang={item.language === 'en' ? 'en' : 'zh-CN'}>{item.title}</strong>{item.readerValue && <p>{item.readerValue}</p>}</a>)}</div>
      </section>}

      <VocabularyDialog
        lookup={vocabularyLookup.lookup}
        onClose={vocabularyLookup.close}
        onToggleSaved={(saved) => void toggleVocabularySaved(saved)}
        onSavePending={() => void vocabularyLookup.savePending().then((result) => result && setToast('已保存到词汇本，等待补充释义'))}
        onRetry={() => void vocabularyLookup.retry()}
      />
      <CopyFallbackDialog value={copyFallback} onClose={() => setCopyFallback(undefined)} />
      {toast && <div className="action-toast" role="status">{toast}</div>}
    </div>
  )
}
